import Http from '../../common/services/Http';
import { Paged } from '../common/types/Page';
import { VehicleConsumptionsResultDto, VehicleCounterListDto, VehicleDto, VehicleMileageCounterListDto } from './models/VehicleDto';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import CustomFile from 'common/models/CustomFile';
import { VehicleMonthlyCostsDto } from 'api/vehicleMonthlyCosts/models/VehicleMonthlyCostsDto';
import { VehiclesSearchCriteria } from './models/VehiclesSearchCriteria';
import { VehicleConsumptionSearchCriteria } from './models/VehicleConsumptionSearchCriteria';
import { VehicleMonthlyCostsSearchCriteria } from 'api/vehicleMonthlyCosts/models/VehicleMonthlyCostsSearchCriteria';
import { CatalogMaintenanceContractsSearchCriteria } from 'api/catalog/models/CatalogMaintenanceContractsSearchCriteria';
import { VehiclesCatalogsSearchCriteria } from './models/VehiclesCatalogsSearchCriteria';
import { VehicleReplacementDto } from './models/VehicleReplacementDto';
import { VehicleCatalogItem } from './models/VehicleCatalogItem';
import { VehicleFuelCatalogItem } from './models/VehicleFuelCatalogItem';


class VehiclesService {

    public getList(criteria: VehiclesSearchCriteria) {
        return Http.get<Paged<VehicleDto>>('vehicles', criteria);
    }

    public getExpensesTotalsReport(criteria: VehiclesSearchCriteria) {
        return Http.getWithConfig<Blob>(`vehicles/get-expenses-total-report`, criteria, { responseType: 'blob' });
    }

    public getExpensesDetailsReport(criteria: VehiclesSearchCriteria) {
        return Http.getWithConfig<Blob>(`vehicles/get-expenses-details-report`, criteria, { responseType: 'blob' });
    }

    public getVehiclesDetailsReport(criteria: VehiclesSearchCriteria) {
        return Http.getWithConfig<Blob>(`vehicles/get-vehicles-details-report`, { ...criteria, isToExcel: true }, { responseType: 'blob' });
    }

    public getVehiclesMileageReport(criteria: VehiclesSearchCriteria) {
        return Http.getWithConfig<Blob>(`vehicles/get-vehicles-mileage-report`, { ...criteria, isToExcel: true }, { responseType: 'blob' });
    }

    public catalog(criteria?: VehiclesCatalogsSearchCriteria) {
        return Http.get<SelectValueLabel[]>(`vehicles/catalog`,criteria);
    }

    public catalogWithFeatures(criteria?: VehiclesCatalogsSearchCriteria) {
        return Http.get<VehicleCatalogItem[]>(`vehicles/catalog-with-features`, criteria);
    }

    public catalogWithFuel(criteria?: VehiclesCatalogsSearchCriteria) {
        return Http.get<VehicleFuelCatalogItem[]>(`vehicles/catalog-with-fuel`, criteria);
    }

    public catalogWithDeleted(criteria?: VehiclesCatalogsSearchCriteria) {
        return Http.get<SelectValueLabel[]>(`vehicles/catalog-with-deleted`,criteria);
    }

    public catalogByCharges(criteria: VehiclesCatalogsSearchCriteria) {
        return Http.get<SelectValueLabel[]>(`vehicles/catalog-by-charges`,criteria);
    }

    public catalogByMaintenances(criteria: VehiclesCatalogsSearchCriteria) {
        return Http.get<SelectValueLabel[]>(`vehicles/catalog-by-maintenances`,criteria);
    }

    public catalogByReminders(criteria: VehiclesCatalogsSearchCriteria) {
        return Http.get<SelectValueLabel[]>(`vehicles/catalog-by-reminders`,criteria);
    }

    public catalogByVisitsReports(criteria: VehiclesCatalogsSearchCriteria) {
        return Http.get<SelectValueLabel[]>(`vehicles/catalog-by-visits-reports`,criteria);
    }

    public catalogByContracts(criteria: VehiclesCatalogsSearchCriteria) {
        return Http.get<SelectValueLabel[]>(`vehicles/catalog-by-contracts`,criteria);
    }

    public catalogByReservations(criteria?: VehiclesCatalogsSearchCriteria) {
        return Http.get<SelectValueLabel[]>(`vehicles/catalog-by-reservations`,criteria);
    }

    public catalogByDefaultDriver(driverId: string) {
        return Http.get<SelectValueLabel[]>(`vehicles/catalog-by-default-driver/${driverId}`);
    }

    public getById(id: string) {
        return Http.get<VehicleDto>('vehicles/' + id);
    }

    public create(model: VehicleDto, photo?: CustomFile[]) {
        return Http.postWithFile<string>('vehicles', model, photo ? photo : []);
    }

    public update(model: VehicleDto, photo?: CustomFile[]) {
        return Http.putWithFile('vehicles', model, photo ? photo : []);
    }

    public remove(model: VehicleDto) {
        return Http.put('vehicles/deactivate', model);
    }

    public activate(model: VehicleDto) {
        return Http.put('vehicles/activate', model);
    }

    public getFuelList() {
        return Http.get<SelectValueLabel[]>('vehicles/getFuelList');
    }

    public getColorsList() {
        return Http.get<SelectValueLabel[]>('vehicles/getColorsList');
    }

    public getConsumptionCounters(criteria: VehicleConsumptionSearchCriteria) {
        return Http.get<VehicleConsumptionsResultDto>('vehicles/consumption-counters', criteria);
    }

    public getCountersList(criteria: VehiclesSearchCriteria) {
        return Http.get<VehicleCounterListDto>('vehicles/counters-list', criteria);
    }

    public getMileageCountersList(criteria: VehiclesSearchCriteria) {
        return Http.get<VehicleMileageCounterListDto>('vehicles/mileage-counters-list', criteria);
    }
    
    public getMonthlyCosts(criteria: VehicleMonthlyCostsSearchCriteria) {
        return Http.get<Paged<VehicleMonthlyCostsDto>>('vehicles/monthly-costs', criteria);
    }

    public getCostsCounters(criteria: VehicleMonthlyCostsSearchCriteria) {
        return Http.get<VehicleMonthlyCostsDto>('vehicles/monthly-costs-counters', criteria);
    }

    public getByMaintenanceContractId(criteria?: CatalogMaintenanceContractsSearchCriteria) {
        return Http.get<SelectValueLabel[]>('vehicles/get-by-maintenance-contract-id', criteria);
    }

    public getCarCurrentDriver(id: string) {
        return Http.get<SelectValueLabel>('vehicles/get-car-current-driver/' + id);
    }

    public candAddVehicles() {
        return Http.get<boolean>('vehicles/can-add-vehicles');
    }
    
    public companyHaveVehicles() {
        return Http.get<boolean | null>('vehicles/company-have-vehicles');
    }
 
    public getVehicleSegmentFeatures(id: string) {
        return Http.get<string[]>('vehicles/getSegmentFeatures/' + id);
    }

    public createReplacement( model: VehicleReplacementDto) {
        return Http.post<string>(`vehiclereplacements/create`, model );
    }

    public finishReplacement(model: VehicleReplacementDto) {
        return Http.put(`vehiclereplacements/update`, model );
    }

    public updateReplacement(model: VehicleReplacementDto) {
        return Http.put(`vehiclereplacements/update`, model );
    }

    public getReplacementHistory(vehicleId: string) {
        return Http.get<any[]>(`vehiclereplacements/history/${vehicleId}`);
    }

    public getReplacementByVehicleId(vehicleId: string) {
        return Http.get<VehicleReplacementDto>(`vehiclereplacements/get-by-vehicle-id/${vehicleId}`);
    }

    public getVehicleHistoryById(vehicleHistoryId: string) {
        return Http.get<VehicleDto>(`vehicles/history/${vehicleHistoryId}`);
    }
    
    public getVehiclesToReplacement(criteria?: VehiclesCatalogsSearchCriteria) {
        return Http.get<SelectValueLabel[]>(`vehicles/get-vehicles-to-replacement`,criteria);
    }

    public prefillCapacityFromGps() {
        return Http.get<{ updated: number }>('vehicles/prefill-capacity-from-gps');
    }
}

export default new VehiclesService();